import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { X, Building2, ChevronRight, ChevronLeft } from 'lucide-react';

interface OfficeData {
  id?: string;
  name: string;
  type: string;
  alternativeName: string;
  phoneNumber: string;
  secondPhoneNumber: string;
  address: string;
  email: string;
  website: string;
  socialMedia: string;
  description: string;
  logoURL: string;
}

interface RoleData {
  subSpecialty: string;
  isOwner: boolean;
  salaryType: number; // 0 for student/intern, 1 for fixed salary, 2 for %, 3 for both
  salaryValue: number;
  profitPercentage: number;
  lawyerTitle: string;
}

interface AddOfficeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (officeData: OfficeData, roleData: RoleData) => void;
  isLoading?: boolean;
}

const AddOfficeModal: React.FC<AddOfficeModalProps> = ({ isOpen, onClose, onSave, isLoading = false }) => {
  const { strings, language, isRTL } = useLocalization();
  
  const [currentStep, setCurrentStep] = useState(1);
  
  const [officeData, setOfficeData] = useState<OfficeData>({
    name: '',
    type: 'مكتب محام مستقل',
    alternativeName: '',
    phoneNumber: '',
    secondPhoneNumber: '',
    address: '',
    email: '',
    website: '',
    socialMedia: '',
    description: '',
    logoURL: ''
  });
  
  const [roleData, setRoleData] = useState<RoleData>({
    subSpecialty: '',
    isOwner: false,
    salaryType: 0,
    salaryValue: 0,
    profitPercentage: 0,
    lawyerTitle: ''
  });

  // Entity types (matching mobile app)
  const entityTypes = [
    { id: '1', name: 'مكتب محام مستقل', nameEn: 'Independent Law Office' },
    { id: '2', name: 'شركة محاماة', nameEn: 'Law Firm' },
    { id: '3', name: 'مكتب استشارات قانونية', nameEn: 'Legal Consultancy Office' },
    { id: '4', name: 'شركة استشارات قانونية', nameEn: 'Legal Consultancy Company' },
    { id: '5', name: 'مكتب محاماة متخصص', nameEn: 'Specialized Law Office' },
    { id: '6', name: 'مكتب محاماة دولي', nameEn: 'International Law Office' },
    { id: '7', name: 'مكتب محاماة عام', nameEn: 'General Law Office' },
    { id: '8', name: 'مكتب محاماة تجاري', nameEn: 'Commercial Law Office' },
    { id: '9', name: 'مكتب محاماة جنائي', nameEn: 'Criminal Law Office' },
    { id: '10', name: 'مكتب محاماة مدني', nameEn: 'Civil Law Office' }
  ];


  // Handle form data changes
  const handleOfficeDataChange = (field: keyof OfficeData, value: string) => {
    setOfficeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleDataChange = (field: keyof RoleData, value: string | boolean | number) => {
    setRoleData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Form validation
  const validateStep = (step: number): string | null => {
    if (step === 1) {
      // Validate office information
      if (!officeData.name.trim()) return strings.offices.officeNameRequired;
      if (!officeData.phoneNumber.trim()) return strings.offices.phoneNumberRequired;
      // Email validation - only if email is provided
      if (officeData.email.trim() && !isValidEmail(officeData.email)) {
        return strings.offices.validEmailAddress;
      }
    }
    
    if (step === 2) {
      // Validate role information
      if (!roleData.subSpecialty) return strings.offices.selectSubSpecialty;
      if (roleData.salaryType === 1 && roleData.salaryValue <= 0) {
        return strings.offices.validSalaryAmount;
      }
      if ((roleData.salaryType === 2 || roleData.salaryType === 3) && roleData.profitPercentage <= 0) {
        return strings.offices.validProfitPercentage;
      }
    }
    
    return null;
  };

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle step navigation
  const handleNext = () => {
    const validationError = validateStep(currentStep);
    if (validationError) {
      alert(validationError);
      return;
    }
    
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle save
  const handleSave = () => {
    const validationError = validateStep(2);
    if (validationError) {
      alert(validationError);
      return;
    }
    
    onSave(officeData, roleData);
    onClose();
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setOfficeData({
        name: '',
        type: 'مكتب محام مستقل',
        alternativeName: '',
        phoneNumber: '',
        secondPhoneNumber: '',
        address: '',
        email: '',
        website: '',
        socialMedia: '',
        description: '',
        logoURL: ''
      });
      setRoleData({
        subSpecialty: '',
        isOwner: false,
        salaryType: 0,
        salaryValue: 0,
        profitPercentage: 0,
        lawyerTitle: ''
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary-dark px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <Building2 className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">{strings.myOffices.addNewOffice}</h2>
                <p className="text-sm opacity-90">
                  {currentStep === 1 && strings.myOffices.step2Title}
                  {currentStep === 2 && strings.myOffices.step3Title}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="text-white hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress indicator */}
          <div className="mt-4 flex items-center space-x-2 rtl:space-x-reverse">
            {[1, 2].map((step) => (
              <div
                key={step}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step <= currentStep
                    ? 'bg-white text-primary'
                    : 'bg-white bg-opacity-20 text-white'
                }`}
              >
                {step}
              </div>
            ))}
            <div className="flex-1 h-1 bg-white bg-opacity-20 rounded-full">
              <div
                className="h-full bg-white rounded-full transition-all duration-300"
                style={{ width: `${((currentStep - 1) / 1) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Step 1: Office Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  {strings.myOffices.step2Title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {strings.myOffices.step2Description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       {/* Office Name */}
                       <div className="md:col-span-2">
                         <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                           {strings.offices.officeName} <span className="text-red-500">*</span>
                         </label>
                  <input
                    type="text"
                    value={officeData.name}
                    onChange={(e) => handleOfficeDataChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                    required
                  />
                </div>

                {/* Entity Type */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myOffices.entityType}
                  </label>
                  <select
                    value={officeData.type}
                    onChange={(e) => handleOfficeDataChange('type', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    {entityTypes.map((type) => (
                      <option key={type.id} value={language === 'ar' ? type.name : type.nameEn}>
                        {language === 'ar' ? type.name : type.nameEn}
                      </option>
                    ))}
                  </select>
                </div>

                       {/* Alternative Name */}
                       <div className="md:col-span-2">
                         <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                           {strings.offices.alternativeName}
                         </label>
                  <input
                    type="text"
                    value={officeData.alternativeName}
                    onChange={(e) => handleOfficeDataChange('alternativeName', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myOffices.phoneNumber} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={officeData.phoneNumber}
                    onChange={(e) => handleOfficeDataChange('phoneNumber', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                    required
                  />
                </div>

                {/* Second Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myOffices.secondPhoneNumber}
                  </label>
                  <input
                    type="tel"
                    value={officeData.secondPhoneNumber}
                    onChange={(e) => handleOfficeDataChange('secondPhoneNumber', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myOffices.address}
                  </label>
                  <input
                    type="text"
                    value={officeData.address}
                    onChange={(e) => handleOfficeDataChange('address', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                       {/* Email */}
                       <div className="md:col-span-2">
                         <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                           {strings.offices.optionalEmail}
                         </label>
                  <input
                    type="email"
                    value={officeData.email}
                    onChange={(e) => handleOfficeDataChange('email', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                    placeholder="office@example.com"
                  />
                </div>

                       {/* Description */}
                       <div className="md:col-span-2">
                         <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                           {strings.offices.officeDescription}
                         </label>
                  <textarea
                    value={officeData.description}
                    onChange={(e) => handleOfficeDataChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    dir={isRTL ? 'rtl' : 'ltr'}
                           placeholder={strings.offices.briefDescription}
                  />
                </div>

                       {/* Logo URL */}
                       <div className="md:col-span-2">
                         <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                           {strings.offices.logoURL}
                         </label>
                  <input
                    type="url"
                    value={officeData.logoURL}
                    onChange={(e) => handleOfficeDataChange('logoURL', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                           placeholder={strings.offices.logoPlaceholder}
                  />
                </div>

                {/* Website */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myOffices.website}
                  </label>
                  <input
                    type="url"
                    value={officeData.website}
                    onChange={(e) => handleOfficeDataChange('website', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                {/* Social Media */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myOffices.socialMedia}
                  </label>
                  <input
                    type="text"
                    value={officeData.socialMedia}
                    onChange={(e) => handleOfficeDataChange('socialMedia', e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Role Assignment */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  {strings.myOffices.step3Title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {strings.myOffices.step3Description}
                </p>
              </div>

              {/* Sub-specialty */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.myOffices.subSpecialty} <span className="text-red-500">*</span>
                </label>
                <select
                  value={roleData.subSpecialty}
                  onChange={(e) => handleRoleDataChange('subSpecialty', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  dir={isRTL ? 'rtl' : 'ltr'}
                  required
                >
                  <option value="">{strings.offices.selectSubSpecialty}</option>
                  <option value="personal_status">{strings.offices.personalStatus}</option>
                  <option value="commercial">{strings.offices.commercial}</option>
                  <option value="criminal">{strings.offices.criminal}</option>
                  <option value="civil">{strings.offices.civil}</option>
                  <option value="administrative">{strings.offices.administrative}</option>
                  <option value="labor">{strings.offices.labor}</option>
                  <option value="tax">{strings.offices.tax}</option>
                  <option value="real_estate">{strings.offices.realEstate}</option>
                  <option value="intellectual_property">{strings.offices.intellectualProperty}</option>
                  <option value="family">{strings.offices.family}</option>
                </select>
              </div>

              {/* Lawyer Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.offices.lawyerTitle}
                </label>
                <input
                  type="text"
                  value={roleData.lawyerTitle}
                  onChange={(e) => handleRoleDataChange('lawyerTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  dir={isRTL ? 'rtl' : 'ltr'}
                  placeholder="e.g., Senior Partner, Associate, etc."
                />
              </div>

              {/* Is Owner */}
              <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
                <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-4">
                  {strings.myOffices.isOwner}
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 rtl:space-x-reverse cursor-pointer">
                    <input
                      type="radio"
                      name="isOwner"
                      checked={roleData.isOwner === true}
                      onChange={() => handleRoleDataChange('isOwner', true)}
                      className="w-4 h-4 text-primary focus:ring-primary"
                    />
                    <span className="text-slate-700 dark:text-slate-300">{strings.myOffices.yes}</span>
                  </label>
                  <label className="flex items-center space-x-3 rtl:space-x-reverse cursor-pointer">
                    <input
                      type="radio"
                      name="isOwner"
                      checked={roleData.isOwner === false}
                      onChange={() => handleRoleDataChange('isOwner', false)}
                      className="w-4 h-4 text-primary focus:ring-primary"
                    />
                    <span className="text-slate-700 dark:text-slate-300">{strings.myOffices.no}</span>
                  </label>
                </div>
              </div>

              {/* Salary Configuration */}
              <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
                <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-4">
                  {strings.offices.salaryConfiguration}
                </h4>
                
                {/* Salary Type */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.offices.salaryType}
                  </label>
                  <select
                    value={roleData.salaryType}
                    onChange={(e) => handleRoleDataChange('salaryType', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value={0}>{strings.offices.studentIntern}</option>
                    <option value={1}>{strings.offices.fixedSalary}</option>
                    <option value={2}>{strings.offices.percentageBased}</option>
                    <option value={3}>{strings.offices.fixedAndPercentage}</option>
                  </select>
                </div>

                {/* Salary Value */}
                {(roleData.salaryType === 1 || roleData.salaryType === 3) && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {strings.offices.fixedSalaryEGP}
                    </label>
                    <input
                      type="number"
                      value={roleData.salaryValue}
                      onChange={(e) => handleRoleDataChange('salaryValue', parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      min="0"
                      step="100"
                    />
                  </div>
                )}

                {/* Profit Percentage */}
                {(roleData.salaryType === 2 || roleData.salaryType === 3) && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {strings.offices.profitPercentage}
                    </label>
                    <input
                      type="number"
                      value={roleData.profitPercentage}
                      onChange={(e) => handleRoleDataChange('profitPercentage', parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 dark:bg-slate-700 px-6 py-4 flex items-center justify-between">
          <div className="flex space-x-3 rtl:space-x-reverse">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                disabled={isLoading}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors flex items-center space-x-2 rtl:space-x-reverse disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRTL ? (
                  <ChevronRight className="w-4 h-4" />
                ) : (
                  <ChevronLeft className="w-4 h-4" />
                )}
                <span>{strings.myOffices.previous}</span>
              </button>
            )}
          </div>
          
          <div className="flex space-x-3 rtl:space-x-reverse">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-6 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {strings.myOffices.cancel}
            </button>
            
            {currentStep < 2 ? (
              <button
                onClick={handleNext}
                disabled={isLoading}
                className="px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors flex items-center space-x-2 rtl:space-x-reverse disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{strings.myOffices.next}</span>
                {isRTL ? (
                  <ChevronLeft className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <span>{strings.myOffices.save}</span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOfficeModal;
